import { useEffect, useMemo, useState } from "react";
import { Plus, Search, X, KeyRound, ShieldOff, ShieldCheck, Mail } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ROLES = ["admin", "supervisor", "crew", "contractor"];

function RoleBadge({ role }) {
    const cls = {
        admin: "badge-admin",
        supervisor: "badge-supervisor",
        crew: "badge-crew",
        contractor: "badge-contractor",
    }[role] || "badge-muted";
    return <span className={`badge-pill ${cls}`}>{role}</span>;
}

export default function Users() {
    const { user: me } = useAuth();
    const isAdmin = me?.role === "admin";

    const [users, setUsers] = useState(null);
    const [err, setErr] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [q, setQ] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [createInfo, setCreateInfo] = useState(null); // {user, temporary_password}
    const [resetInfo, setResetInfo] = useState(null); // {email, temporary_password}

    const load = async () => {
        setErr("");
        try {
            const { data } = await api.get("/admin/users", {
                params: {
                    ...(roleFilter ? { role: roleFilter } : {}),
                    ...(q.trim() ? { q: q.trim() } : {}),
                },
            });
            setUsers(data.users);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleFilter]);

    const counts = useMemo(() => {
        const c = { all: users?.length || 0 };
        ROLES.forEach((r) => (c[r] = 0));
        (users || []).forEach((u) => (c[u.role] = (c[u.role] || 0) + 1));
        return c;
    }, [users]);

    const toggleActive = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
            load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    const changeRole = async (u, role) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { role });
            load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    const resetPw = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
            setResetInfo({ email: u.email, temporary_password: data.temporary_password });
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Team</p>
                <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
                    <div>
                        <h1 className="h1" data-testid="team-title">
                            Crew &amp; contractors
                        </h1>
                        <p className="text-muted mt-2 max-w-[640px]">
                            Add team members, manage roles, and trigger password resets when
                            someone needs back in.
                        </p>
                    </div>
                    {isAdmin && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowCreate(true)}
                            data-testid="team-add-btn"
                        >
                            <Plus size={16} />
                            Add team member
                        </button>
                    )}
                </div>
            </header>

            {/* Filters */}
            <div className="mt-6 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    className={`btn ${roleFilter === "" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => setRoleFilter("")}
                    data-testid="role-filter-all"
                >
                    All ({counts.all})
                </button>
                {ROLES.map((r) => (
                    <button
                        key={r}
                        type="button"
                        className={`btn ${roleFilter === r ? "btn-primary" : "btn-ghost"}`}
                        onClick={() => setRoleFilter(r)}
                        data-testid={`role-filter-${r}`}
                    >
                        <span className="capitalize">{r}</span>
                        <span className="text-xs opacity-70">({counts[r] || 0})</span>
                    </button>
                ))}
            </div>

            <form
                className="mt-4 flex gap-2 flex-wrap"
                onSubmit={(e) => {
                    e.preventDefault();
                    load();
                }}
            >
                <div className="relative flex-1 min-w-[240px]">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]"
                    />
                    <input
                        type="text"
                        placeholder="Search by name or email"
                        className="field-input"
                        style={{ paddingLeft: "2.25rem" }}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="team-search-input"
                    />
                </div>
                <button type="submit" className="btn btn-secondary" data-testid="team-search-btn">
                    Search
                </button>
            </form>

            {err && (
                <div
                    className="brand-card mt-6"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="team-error"
                >
                    {err}
                </div>
            )}

            <div className="brand-card mt-6 p-0 overflow-hidden fade-up" data-testid="team-card">
                {users === null ? (
                    <div className="p-8 text-muted">Loading team…</div>
                ) : users.length === 0 ? (
                    <div className="p-10 text-center text-muted" data-testid="team-empty">
                        No team members match this filter yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="brand-table" data-testid="team-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Contact</th>
                                    <th>Role</th>
                                    <th>Onboarding</th>
                                    <th>Status</th>
                                    {isAdmin && <th className="text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr
                                        key={u.id}
                                        style={{ cursor: "default" }}
                                        data-testid={`user-row-${u.id}`}
                                    >
                                        <td>
                                            <p className="font-display font-bold">{u.name || "—"}</p>
                                            <p className="text-xs text-muted">{u.email}</p>
                                        </td>
                                        <td className="text-sm text-muted">{u.phone || "—"}</td>
                                        <td>
                                            {isAdmin && u.id !== me?.id ? (
                                                <select
                                                    className="field-select"
                                                    value={u.role}
                                                    onChange={(e) => changeRole(u, e.target.value)}
                                                    data-testid={`user-role-select-${u.id}`}
                                                    style={{ padding: "0.35rem 0.6rem" }}
                                                >
                                                    {ROLES.map((r) => (
                                                        <option key={r} value={r} className="capitalize">
                                                            {r}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <RoleBadge role={u.role} />
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className={`badge-pill ${u.onboarding_status === "complete" ? "badge-success" : "badge-warning"}`}
                                            >
                                                {u.onboarding_status}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={`badge-pill ${u.is_active ? "badge-success" : "badge-muted"}`}
                                            >
                                                {u.is_active ? "active" : "deactivated"}
                                            </span>
                                        </td>
                                        {isAdmin && (
                                            <td className="text-right">
                                                <div className="inline-flex items-center gap-1 justify-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost"
                                                        title="Reset password"
                                                        onClick={() => resetPw(u)}
                                                        data-testid={`user-reset-${u.id}`}
                                                    >
                                                        <KeyRound size={14} />
                                                    </button>
                                                    {u.id !== me?.id && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost"
                                                            title={
                                                                u.is_active
                                                                    ? "Deactivate"
                                                                    : "Reactivate"
                                                            }
                                                            onClick={() => toggleActive(u)}
                                                            data-testid={`user-toggle-${u.id}`}
                                                        >
                                                            {u.is_active ? (
                                                                <ShieldOff size={14} />
                                                            ) : (
                                                                <ShieldCheck size={14} />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create modal */}
            {showCreate && (
                <CreateUserModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(info) => {
                        setShowCreate(false);
                        setCreateInfo(info);
                        load();
                    }}
                />
            )}

            {/* Success modal: temporary password */}
            {createInfo && (
                <TempPasswordModal
                    title="Team member added"
                    body={
                        <>
                            <p>
                                <strong>{createInfo.user.name}</strong> ({createInfo.user.email})
                                was added as {createInfo.user.role}.
                            </p>
                            <p className="text-muted text-sm mt-2">
                                Postmark email is not configured yet, so we generated a
                                temporary password to share manually:
                            </p>
                        </>
                    }
                    password={createInfo.temporary_password}
                    onClose={() => setCreateInfo(null)}
                />
            )}
            {resetInfo && (
                <TempPasswordModal
                    title="Password reset"
                    body={
                        <p>
                            New temporary password for{" "}
                            <strong>{resetInfo.email}</strong>:
                        </p>
                    }
                    password={resetInfo.temporary_password}
                    onClose={() => setResetInfo(null)}
                />
            )}
        </div>
    );
}

function CreateUserModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        email: "",
        name: "",
        role: "crew",
        phone: "",
        password: "",
    });
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            const payload = {
                email: form.email.trim(),
                name: form.name.trim(),
                role: form.role,
                phone: form.phone.trim(),
            };
            if (form.password.trim()) payload.password = form.password.trim();
            const { data } = await api.post("/admin/users", payload);
            onCreated(data);
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
            style={{ background: "rgba(26,26,26,0.5)" }}
            onClick={onClose}
            data-testid="create-user-modal"
        >
            <form
                className="brand-card max-w-[460px] w-full"
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
            >
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <p className="eyebrow">Add</p>
                        <h2 className="h2 mt-2">New team member</h2>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="modal-close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="mt-5 flex flex-col gap-4">
                    <div>
                        <label className="field-label" htmlFor="cu-name">
                            Full name
                        </label>
                        <input
                            id="cu-name"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="field-input"
                            data-testid="cu-name"
                        />
                    </div>
                    <div>
                        <label className="field-label" htmlFor="cu-email">
                            Email
                        </label>
                        <input
                            id="cu-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="field-input"
                            data-testid="cu-email"
                        />
                    </div>
                    <div>
                        <label className="field-label" htmlFor="cu-phone">
                            Phone
                        </label>
                        <input
                            id="cu-phone"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="field-input"
                            data-testid="cu-phone"
                        />
                    </div>
                    <div>
                        <label className="field-label" htmlFor="cu-role">
                            Role
                        </label>
                        <select
                            id="cu-role"
                            className="field-select"
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            data-testid="cu-role"
                        >
                            {ROLES.map((r) => (
                                <option key={r} value={r} className="capitalize">
                                    {r}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="field-label" htmlFor="cu-pw">
                            Temporary password (optional)
                        </label>
                        <input
                            id="cu-pw"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="field-input"
                            placeholder="Leave empty to auto-generate"
                            data-testid="cu-password"
                        />
                    </div>
                    {err && (
                        <p className="field-error" data-testid="cu-error">
                            {err}
                        </p>
                    )}
                    <button
                        type="submit"
                        className="btn btn-primary w-full"
                        disabled={saving}
                        data-testid="cu-submit"
                    >
                        <Mail size={14} />
                        {saving ? "Saving…" : "Add member"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function TempPasswordModal({ title, body, password, onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
            style={{ background: "rgba(26,26,26,0.5)" }}
            onClick={onClose}
            data-testid="temp-password-modal"
        >
            <div
                className="brand-card max-w-[460px] w-full"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <p className="eyebrow">Done</p>
                        <h2 className="h2 mt-2">{title}</h2>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="mt-4 text-sm">{body}</div>
                <div
                    className="mt-4 p-3 rounded-md font-mono text-sm break-all"
                    style={{
                        background: "rgba(210,5,28,0.06)",
                        border: "1.5px dashed var(--brand-red)",
                        color: "var(--brand-red)",
                    }}
                    data-testid="temp-password-value"
                >
                    {password}
                </div>
                <button
                    type="button"
                    className="btn btn-primary w-full mt-5"
                    onClick={() => {
                        navigator.clipboard?.writeText(password);
                        onClose();
                    }}
                    data-testid="temp-password-copy"
                >
                    Copy &amp; close
                </button>
            </div>
        </div>
    );
}
